import { ActionsRenderCell } from '@/components/bulkQueue/cells/ActionsRender';
import { FulfillmentWindowRenderCell } from '@/components/bulkQueue/cells/FulfillmentWindowRender';
import { OrderRenderCell } from '@/components/bulkQueue/cells/OrderRender';
import { PatientRenderCell } from '@/components/bulkQueue/cells/PatientRender';
import { PriorityRenderCell } from '@/components/bulkQueue/cells/PriorityRender';
import {
    ClinicianSelectionCell,
    ClinicianSelectionHeader,
} from '@/components/bulkQueue/cells/clinician/ClinicianSelection';
import { DateRenderCell } from '@/components/bulkQueue/cells/date/DateRender';
import { ServicesRenderCell } from '@/components/bulkQueue/cells/services/ServicesRender';
import { BulkClinicianAssignmentTableQuery } from '@/components/bulkQueue/tables/clinician/BulkClinicianAssignmentTable.gql.types';
import { BulkClinicianContextProvider } from '@/components/bulkQueue/tables/clinician/BulkClinicianContext';
import { getSetCellProps } from '@/components/bulkQueue/tables/components';
import { VisitRequestTableRow, VisitTableRow } from '@/components/bulkQueue/tables/queries';
import { useConnectedBulkTable } from '@/components/bulkQueue/tables/useConnectedBulkTable';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { BulkTableRow } from '@/components/bulkQueue/types';
import { rowToLink } from '@/components/bulkQueue/utils';
import { MapFriendlyVisit } from '@/components/mapping/VisitsMap/VisitsMap.types';
import { useSetMapFriendlyVisits } from '@/components/mapping/VisitsMap/useSetMapFriendlyVisits';
import { AxleTable, RawColumn, ServerSideSort } from '@/components/tables/AxleTable';
import { VisitColumns } from '@/components/visits/tables/columns';
import { visitOrVisitRequestTableRowFragmentToMapFriendlyVisits } from '@/components/visits/utils/visitOrVisitRequestTableRowFragmentToMapFriendlyVisits';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { FC, useCallback } from 'react';

export const BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY = gql`
    query BulkClinicianAssignmentTable(
        $visitFilters: VisitFilter
        $visitPagination: PaginationInput
        $visitOrdering: VisitOrdering
        $visitsIncluded: Boolean!
        $visitRequestFilters: VisitRequestFilter
        $visitRequestPagination: PaginationInput
        $visitRequestOrdering: VisitRequestOrdering
        $visitRequestsIncluded: Boolean!
    ) {
        visitRequests(
            filters: $visitRequestFilters
            pagination: $visitRequestPagination
            ordering: $visitRequestOrdering
        ) @include(if: $visitRequestsIncluded) {
            totalCount
            items {
                ...VisitRequestTableRow
            }
        }
        visits(filters: $visitFilters, pagination: $visitPagination, ordering: $visitOrdering)
            @include(if: $visitsIncluded) {
            totalCount
            items {
                ...VisitTableRow
            }
        }
    }
    ${VisitRequestTableRow}
    ${VisitTableRow}
`;

type BulkClinicianAssignmentTableProps = {
    title?: string;
    rows: BulkTableRow[];
    pagination: Pagination;
    serverSideSort: ServerSideSort;
};

const COLUMNS: RawColumn<BulkTableRow>[] = [
    {
        field: 'sortPriority',
        label: 'Priority',
        rowToComponent: (entity) => <PriorityRenderCell entity={entity} />,
        options: { ...VisitColumns.priorityColumnOptions, sort: false },
    },
    {
        field: 'patient.fullName',
        label: 'Patient',
        rowToComponent: (entity) => <PatientRenderCell entity={entity} />,
    },
    {
        field: 'sortServicesName',
        label: 'Services',
        rowToComponent: (entity) => <ServicesRenderCell entity={entity} />,
        options: { sort: false },
    },
    {
        field: 'cliniciansSortKey',
        label: 'Clinician',
        rowToComponent: (entity) => <ClinicianSelectionCell entity={entity} />,
        options: {
            sort: false,
            setCellProps: () => getSetCellProps(true),
            customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
                <ClinicianSelectionHeader
                    key={columnMeta.name}
                    columnMeta={columnMeta}
                    handleToggleColumn={handleToggleColumn}
                    sortOrder={sortOrder}
                />
            ),
        },
    },
    {
        field: 'sortDate',
        label: 'Date',
        rowToComponent: (entity) => <DateRenderCell entity={entity} />,
    },
    {
        field: 'fulfillmentWindow.startDate',
        label: 'Fulfillment Window',
        rowToComponent: (entity) => <FulfillmentWindowRenderCell entity={entity} />,
    },
    {
        field: 'orderSortKey',
        label: 'Order',
        rowToComponent: (entity) => <OrderRenderCell entity={entity} />,
        options: { sort: false },
    },
    {
        field: '',
        label: 'Actions',
        rowToComponent: (entity) => <ActionsRenderCell entity={entity} />,
        options: { sort: false },
    },
];

export const BulkClinicianAssignmentTable: FC<BulkClinicianAssignmentTableProps> = (props) => {
    const { rows, pagination, title, serverSideSort } = props;

    return (
        <AxleTable
            title={
                typeof title === 'string' ? (
                    <Typography variant='h2'>{title}</Typography>
                ) : undefined
            }
            dynamicPaging={pagination}
            data={rows}
            rowToLink={rowToLink}
            columns={COLUMNS}
            toolbar={false}
            serverSideSort={serverSideSort}
        />
    );
};

type ConnectedBulkClinicianAssignmentTableProps = {
    title?: string;
};

export const ConnectedBulkClinicianAssignmentTable: FC<
    ConnectedBulkClinicianAssignmentTableProps
> = (props) => {
    const { title } = props;
    const { rows, pagination, serverSideSort, showSkeleton } =
        useConnectedBulkTable<BulkClinicianAssignmentTableQuery>({
            query: BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY,
        });

    const setMapFriendlyVisits: () => MapFriendlyVisit[] = useCallback(() => {
        return visitOrVisitRequestTableRowFragmentToMapFriendlyVisits(rows);
    }, [rows]);

    useSetMapFriendlyVisits(setMapFriendlyVisits);

    if (showSkeleton) {
        return <SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />;
    }

    return (
        <BulkClinicianContextProvider entities={rows}>
            <BulkClinicianAssignmentTable
                rows={rows}
                title={title}
                pagination={pagination}
                serverSideSort={serverSideSort}
            />
        </BulkClinicianContextProvider>
    );
};
